"use client";

import {
  State,
  Subscription,
  SubscriptionOptions,
  SubscriptionState,
} from "centrifuge";
import React from "react";
import { useRealtimeContext } from "./context";
import {
  BaseChannel,
  ChannelMessage,
  MessageCallbackFn,
  StateUpdateCallbackFn,
} from "./types";

type UseSubscriptionOpts<
  CPrefix extends string,
  CMap extends Record<CPrefix, any>,
  Channel extends BaseChannel<CPrefix>,
> = {
  enabled?: boolean;
  token?: string;
  getToken?: () => Promise<string>;
  onMessage?: MessageCallbackFn<CPrefix, CMap, Channel>;
  onChange?: StateUpdateCallbackFn;
  subscriptionOpts?: Partial<Omit<SubscriptionOptions, "getToken">>;
};

// subscription.ts
function createUseSubscriptionHook<
  CPrefix extends string,
  CMap extends Record<CPrefix, any>,
>() {
  return function useSubscription<Channel extends BaseChannel<CPrefix>>(
    channel: Channel,
    opts: UseSubscriptionOpts<CPrefix, CMap, Channel>,
  ) {
    const { enabled = true, token = "", getToken, onMessage, onChange } = opts;
    const [sub, setSub] = React.useState<Subscription | undefined>(undefined);
    const { connection, connectionState } = useRealtimeContext();

    React.useEffect(() => {
      if (connection === undefined || sub !== undefined) return;

      const newSub =
        connection.getSubscription(channel) ??
        connection.newSubscription(channel, {
          token,
          getToken,
        });

      newSub.on("publication", (ctx) => {
        console.debug(
          `[useSubscription] ${channel} - new message: ${JSON.stringify(ctx.data)}`,
        );
        onMessage?.(ctx.data as ChannelMessage<CPrefix, CMap, Channel>);
      });

      newSub.on("state", (ctx) => {
        console.debug(
          `[useSubscription] ${channel} - state changed: ${ctx.newState}`,
        );
        onChange?.(ctx.newState);
      });

      setSub(newSub);
    }, [connection, token]);

    React.useEffect(() => {
      console.debug(
        `[useSubscription] ${channel} - ${sub?.state} ${connectionState} ${enabled}`,
      );
      if (sub === undefined) return;

      if (connectionState === State.Disconnected) {
        connection?.connect();
      } else if (
        connectionState === State.Connected &&
        sub.state === SubscriptionState.Unsubscribed &&
        enabled === true
      ) {
        sub.subscribe();
      }
    }, [sub, connectionState, enabled]);

    return {
      sub,
      connection,
    };
  };
}

export { createUseSubscriptionHook };
